// Extended role system with 10 roles and granular permissions

export type AppRole = 
  | 'ceo'
  | 'projektleitung'
  | 'sachbearbeiter'
  | 'finanzen'
  | 'marketing'
  | 'it_admin'
  | 'datenverwalter'
  | 'verwaltung'
  | 'designer'
  | 'praktikant';

export type AccessLevel = 'full' | 'limited' | 'none';

export type ModuleId = 
  | 'uebersicht'
  | 'team_chat'
  | 'projekte'
  | 'aufgaben'
  | 'daten'
  | 'finanzen'
  | 'versicherungen'
  | 'marketing'
  | 'verwaltung'
  | 'team'
  | 'system'
  | 'einstellungen'
  | 'kalender'
  | 'ki_wissen'
  | 'personal'
  | 'kontakte'
  | 'email'
  | 'integrationen'
  | 'nas_storage';

// Role labels in German
export const ROLE_LABELS: Record<AppRole, string> = {
  ceo: 'Geschäftsführer / CEO',
  projektleitung: 'Projektleitung / Teamleitung',
  sachbearbeiter: 'Fachkraft / Sachbearbeiter',
  finanzen: 'Finanzmanager / Buchhalter',
  marketing: 'Marketing / Social Media',
  it_admin: 'IT-Support / Systemadministrator',
  datenverwalter: 'Datenverwalter / Data Manager',
  verwaltung: 'Verwaltung / Office Management',
  designer: 'Designer / UX / Content',
  praktikant: 'Service-Mitarbeiter / Praktikanten',
};

// Role descriptions explaining what each role can do
export const ROLE_DESCRIPTIONS: Record<AppRole, string> = {
  ceo: 'Vollständiger Zugriff auf alle Bereiche. Kann Benutzer verwalten, Rollen zuweisen und alle Einstellungen ändern.',
  projektleitung: 'Leitet Projekte und Teams. Voller Zugriff auf Projekte, Aufgaben, Kalender und Team-Kommunikation. Kann Teammitglieder Aufgaben zuweisen.',
  sachbearbeiter: 'Bearbeitet zugewiesene Aufgaben und Projekte. Grundlegender Zugriff auf Team-Chat und Kalender.',
  finanzen: 'Verwaltet alle finanziellen Aspekte. Vollzugriff auf Finanzen, Buchungen und Budgets. Eingeschränkter Zugriff auf Projekte.',
  marketing: 'Verwaltet Marketing-Kampagnen, Social Media und Branding. Zugriff auf Marketing-Bereich und Kontakte.',
  it_admin: 'Systemadministrator mit Vollzugriff auf technische Bereiche. Kann Benutzer und Berechtigungen verwalten.',
  datenverwalter: 'Verwaltet Daten, Dateien und Dokumente. Vollzugriff auf den Datenbereich und Dateiorganisation.',
  verwaltung: 'Unterstützt die Geschäftsführung mit Zugriff auf Kalender, Kontakte, Projekte und Team-Chat. Kann Termine und Aufgaben koordinieren.',
  designer: 'Gestaltet visuelle Inhalte. Zugriff auf Design-relevante Projekte, Aufgaben und Marketing-Assets.',
  praktikant: 'Eingeschränkter Zugriff für Einarbeitung. Kann zugewiesene Aufgaben sehen und im Team-Chat kommunizieren.',
};

// Short role labels for badges
export const ROLE_SHORT_LABELS: Record<AppRole, string> = {
  ceo: 'CEO',
  projektleitung: 'Projektleitung',
  sachbearbeiter: 'Sachbearbeiter',
  finanzen: 'Finanzen',
  marketing: 'Marketing',
  it_admin: 'IT Admin',
  datenverwalter: 'Datenverwalter',
  verwaltung: 'Verwaltung',
  designer: 'Designer',
  praktikant: 'Praktikant',
};

// Module labels in German
export const MODULE_LABELS: Record<ModuleId, string> = {
  uebersicht: 'Übersicht',
  team_chat: 'Team-Chat',
  projekte: 'Projekte',
  aufgaben: 'Aufgaben',
  daten: 'Daten',
  finanzen: 'Finanzen',
  versicherungen: 'Versicherungen',
  marketing: 'Marketing',
  verwaltung: 'Verwaltung',
  team: 'Team',
  system: 'System',
  einstellungen: 'Einstellungen',
  kalender: 'Kalender',
  ki_wissen: 'KI-Wissen',
  personal: 'Personal',
  kontakte: 'Kontakte',
  email: 'E-Mail',
  integrationen: 'Integrationen',
  nas_storage: 'NAS & Server',
};

// Access level labels
export const ACCESS_LEVEL_LABELS: Record<AccessLevel, string> = {
  full: 'Vollzugriff',
  limited: 'Eingeschränkt',
  none: 'Kein Zugriff',
};

// Access level icons (for UI)
export const ACCESS_LEVEL_ICONS: Record<AccessLevel, string> = {
  full: '✓',
  limited: '⚡',
  none: '✕',
};

// Detailed explanations for limited access per module
export const LIMITED_ACCESS_EXPLANATIONS: Record<ModuleId, { allowed: string[]; restricted: string[] }> = {
  uebersicht: {
    allowed: ['Dashboard-Statistiken anzeigen', 'Eigene Aufgaben-Übersicht', 'Persönliche Aktivitäten'],
    restricted: ['Team-weite Statistiken', 'Finanz-Übersichten', 'System-Status'],
  },
  team_chat: {
    allowed: ['Nachrichten lesen & senden', 'An Gruppen-Chats teilnehmen', 'Dateien teilen'],
    restricted: ['Nachrichten anderer löschen', 'Chat-Verläufe exportieren'],
  },
  projekte: {
    allowed: ['Zugewiesene Projekte ansehen', 'Projekt-Details lesen', 'Eigene Notizen hinzufügen'],
    restricted: ['Neue Projekte erstellen', 'Projekte löschen', 'Projekt-Einstellungen ändern', 'Alle Projekte sehen'],
  },
  aufgaben: {
    allowed: ['Zugewiesene Aufgaben sehen', 'Status eigener Aufgaben ändern', 'Kommentare hinzufügen'],
    restricted: ['Aufgaben anderen zuweisen', 'Aufgaben löschen', 'Alle Aufgaben sehen'],
  },
  daten: {
    allowed: ['Freigegebene Dateien ansehen', 'Eigene Dateien hochladen', 'Download erlaubter Dokumente'],
    restricted: ['Ordnerstruktur ändern', 'Dateien anderer löschen', 'Zugriff auf vertrauliche Bereiche'],
  },
  finanzen: {
    allowed: ['Eigene Buchungen einsehen', 'Grundlegende Finanzberichte'],
    restricted: ['Buchungen erstellen/bearbeiten', 'Konten verwalten', 'Detaillierte Finanzanalysen', 'Export-Funktionen'],
  },
  versicherungen: {
    allowed: ['Versicherungen ansehen', 'Versicherungs-Details lesen', 'Notizen hinzufügen'],
    restricted: ['Versicherungen erstellen/bearbeiten', 'Versicherungen löschen'],
  },
  marketing: {
    allowed: ['Kampagnen-Übersicht sehen', 'Social-Media-Posts ansehen', 'Analytics einsehen'],
    restricted: ['Kampagnen erstellen/bearbeiten', 'Werbebudgets ändern', 'Konten verknüpfen'],
  },
  verwaltung: {
    allowed: ['Eigenes Profil bearbeiten', 'Dokumenten-Anfragen stellen'],
    restricted: ['Benutzer verwalten', 'Rollen zuweisen', 'System-Einstellungen'],
  },
  team: {
    allowed: ['Teammitglieder-Liste sehen', 'Kontaktdaten einsehen', 'Eigene Arbeitszeit sehen'],
    restricted: ['Rollen ändern', 'Berechtigungen anpassen', 'Mitglieder hinzufügen/entfernen'],
  },
  system: {
    allowed: ['System-Status einsehen'],
    restricted: ['Alle System-Aktionen', 'Logs einsehen', 'Wartungsarbeiten durchführen'],
  },
  einstellungen: {
    allowed: ['Eigenes Profil bearbeiten', 'Benachrichtigungen konfigurieren', 'Passwort ändern'],
    restricted: ['Globale Einstellungen', 'API-Schlüssel verwalten', 'Integrationen konfigurieren'],
  },
  kalender: {
    allowed: ['Eigene Termine sehen', 'Persönliche Termine erstellen', 'Team-Termine ansehen'],
    restricted: ['Termine anderer bearbeiten', 'Kalender-Einstellungen ändern'],
  },
  ki_wissen: {
    allowed: ['KI-Assistent nutzen', 'Eigene Konversationen führen'],
    restricted: ['Wissensbasis bearbeiten', 'KI-Einstellungen ändern'],
  },
  personal: {
    allowed: ['Eigene Abwesenheiten verwalten', 'Eigene Arbeitszeiten sehen', 'Urlaubsanträge stellen'],
    restricted: ['Alle Mitarbeiter-Daten sehen', 'Anträge genehmigen', 'Personalakten einsehen'],
  },
  kontakte: {
    allowed: ['Kontakte ansehen', 'Eigene Kontakte hinzufügen', 'Notizen erstellen'],
    restricted: ['Kontakte löschen', 'Kontakte exportieren', 'Massen-Aktionen'],
  },
  email: {
    allowed: ['E-Mails lesen', 'E-Mails senden', 'E-Mail-Postfach einsehen'],
    restricted: ['E-Mail-Konten verwalten', 'E-Mail-Einstellungen ändern'],
  },
  integrationen: {
    allowed: ['Integrationen ansehen', 'Verbindungsstatus einsehen'],
    restricted: ['Integrationen konfigurieren', 'API-Keys verwalten', 'Webhooks erstellen/bearbeiten'],
  },
  nas_storage: {
    allowed: ['NAS-Dateien durchsuchen', 'Dateien herunterladen', 'Verbindungsstatus sehen'],
    restricted: ['NAS-Verbindungen einrichten', 'Verbindungen löschen', 'Server-Einstellungen ändern'],
  },
};

// Role badge colors
export const ROLE_BADGE_COLORS: Record<AppRole, string> = {
  ceo: 'bg-red-500/20 text-red-400 border-red-500/30',
  projektleitung: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  sachbearbeiter: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  finanzen: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  marketing: 'bg-fuchsia-500/20 text-fuchsia-400 border-fuchsia-500/30',
  it_admin: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  datenverwalter: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
  verwaltung: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  designer: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  praktikant: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
};

// Role categories for grouping in UI
export const ROLE_CATEGORIES: { label: string; roles: AppRole[] }[] = [
  {
    label: 'Führung',
    roles: ['ceo', 'projektleitung'],
  },
  {
    label: 'Administration',
    roles: ['it_admin', 'verwaltung', 'datenverwalter'],
  },
  {
    label: 'Fachbereiche',
    roles: ['finanzen', 'marketing', 'designer'],
  },
  {
    label: 'Allgemein',
    roles: ['sachbearbeiter', 'praktikant'],
  },
];

// Module to route mapping
export const MODULE_ROUTES: Record<ModuleId, string> = {
  uebersicht: '/dashboard',
  team_chat: '/dashboard/chat',
  projekte: '/dashboard/projekte',
  aufgaben: '/dashboard/aufgaben',
  kalender: '/dashboard/kalender',
  daten: '/dashboard/daten',
  finanzen: '/dashboard/finanzen',
  versicherungen: '/dashboard/versicherungen',
  marketing: '/dashboard/marketing',
  kontakte: '/dashboard/kontakte',
  email: '/dashboard/email',
  ki_wissen: '/dashboard/ki-chat',
  personal: '/dashboard/personal',
  team: '/dashboard/team',
  system: '/dashboard/system',
  integrationen: '/dashboard/integrationen',
  einstellungen: '/dashboard/einstellungen',
  verwaltung: '/dashboard/team',
  nas_storage: '/dashboard/storage',
};

// Route to module mapping
export const ROUTE_MODULES: Record<string, ModuleId> = {
  '/dashboard': 'uebersicht',
  '/dashboard/chat': 'team_chat',
  '/dashboard/projekte': 'projekte',
  '/dashboard/aufgaben': 'aufgaben',
  '/dashboard/kalender': 'kalender',
  '/dashboard/daten': 'daten',
  '/dashboard/finanzen': 'finanzen',
  '/dashboard/versicherungen': 'versicherungen',
  '/dashboard/marketing': 'marketing',
  '/dashboard/kontakte': 'kontakte',
  '/dashboard/email': 'email',
  '/dashboard/ki-chat': 'ki_wissen',
  '/dashboard/personal': 'personal',
  '/dashboard/team': 'team',
  '/dashboard/system': 'system',
  '/dashboard/integrationen': 'integrationen',
  '/dashboard/einstellungen': 'einstellungen',
  '/dashboard/storage': 'nas_storage',
};

// Helper functions
export const getRoleLabel = (role: AppRole | string): string => {
  return ROLE_SHORT_LABELS[role as AppRole] || role;
};

export const getRoleFullLabel = (role: AppRole): string => {
  return ROLE_LABELS[role] || role;
};

export const getRoleBadgeColor = (role: AppRole | string): string => {
  return ROLE_BADGE_COLORS[role as AppRole] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
};

export const getModuleLabel = (module: ModuleId): string => {
  return MODULE_LABELS[module] || module;
};

export const getAccessLevelLabel = (level: AccessLevel): string => {
  return ACCESS_LEVEL_LABELS[level] || level;
};

export const getAccessLevelIcon = (level: AccessLevel): string => {
  return ACCESS_LEVEL_ICONS[level] || '?';
};

export const getModuleForRoute = (route: string): ModuleId | null => {
  if (ROUTE_MODULES[route]) {
    return ROUTE_MODULES[route];
  }
  for (const [path, module] of Object.entries(ROUTE_MODULES)) {
    if (route.startsWith(path) && path !== '/dashboard') {
      return module;
    }
  }
  return null;
};

export const isAdminRole = (role: AppRole): boolean => {
  return role === 'ceo' || role === 'it_admin';
};

export const canManageUsers = (role: AppRole): boolean => {
  return role === 'ceo' || role === 'it_admin';
};

export const canManagePermissions = (role: AppRole): boolean => {
  return role === 'ceo' || role === 'it_admin';
};

// All available roles for dropdowns
export const ALL_ROLES: AppRole[] = [
  'ceo',
  'projektleitung',
  'sachbearbeiter',
  'finanzen',
  'marketing',
  'it_admin',
  'datenverwalter',
  'verwaltung',
  'designer',
  'praktikant',
];

// Role hierarchy for backwards compatibility
const ROLE_HIERARCHY: Record<AppRole, number> = {
  ceo: 13,
  it_admin: 12,
  projektleitung: 11,
  finanzen: 9,
  datenverwalter: 8,
  verwaltung: 7,
  marketing: 5,
  designer: 4,
  sachbearbeiter: 3,
  praktikant: 1,
};

// Helper for legacy code - checks if user has at least the given role level
export const hasMinimumRole = (userRole: AppRole | null, requiredRole: AppRole | string): boolean => {
  if (!userRole) return false;
  
  const roleMapping: Record<string, AppRole> = {
    admin: 'ceo',
    manager: 'projektleitung',
    mitarbeiter: 'sachbearbeiter',
    viewer: 'praktikant',
  };
  
  const mappedRole = roleMapping[requiredRole] || requiredRole as AppRole;
  
  return (ROLE_HIERARCHY[userRole] || 0) >= (ROLE_HIERARCHY[mappedRole] || 0);
};

// All modules for permission management
export const ALL_MODULES: ModuleId[] = [
  'uebersicht',
  'team_chat',
  'projekte',
  'aufgaben',
  'kalender',
  'daten',
  'finanzen',
  'versicherungen',
  'marketing',
  'kontakte',
  'email',
  'ki_wissen',
  'team',
  'system',
  'integrationen',
  'einstellungen',
  'nas_storage',
];

// Integration modules subset for separate display
export const INTEGRATION_MODULES: ModuleId[] = [
  'nas_storage',
  'integrationen',
];

// Main modules (non-integration) for separate display
export const MAIN_MODULES: ModuleId[] = ALL_MODULES.filter(m => !INTEGRATION_MODULES.includes(m));